import { useEffect, useRef } from "react";
import {
  AccessibilityCategory,
  AccessibilityTag,
  ReviewPhoto,
  VenueDetails,
} from "common/types";
import AnalyticsService from "@/services/analytics";
import { useI18N } from "common/hooks";
import { formatFullDateAsShort } from "common/utils/general";
import AccessibilitySpaceCard from "@/components/common/AccessibilitySpaceCard";

interface LatestReviewDetailUpdatedDate {
  latest_review_posted_at: string;
  latest_review_submitted_by: number;
  latest_review_id: number;
}

type AccessibilitySubItemProps = {
  qtagsAll: string;
  qtags: AccessibilityTag[];
  sections: any;
  reviewPhotos: ReviewPhoto[];
  fullSize?: boolean;
  venueId: string;
  venueDetails: VenueDetails;
  onFlag: (mainTag: any) => void;
  lastUpdated: string;
  categories: AccessibilityCategory[];
  cid?: string;
  tid?: string;
  onShowDetailView?: (initialSpaceId: string) => void;
  latestReviewDetailUpdatedDate?: LatestReviewDetailUpdatedDate;
};

function AccessibilitySubItem({
  qtags,
  qtagsAll,
  venueDetails,
  reviewPhotos,
  categories,
  cid,
  tid,
  onShowDetailView,
  latestReviewDetailUpdatedDate,
}: AccessibilitySubItemProps) {
  const i18n = useI18N();
  const hasAutoOpened = useRef(false);

  // Calculate the initial space id for the detail view based on current qtagsAll
  const getInitialSpaceId = (): string => {
    for (const category of categories) {
      const tagIndex = category.tags.findIndex(
        (tag) => tag.qtagsAll === qtagsAll,
      );
      if (tagIndex !== -1) {
        return `${category.catId}-${tagIndex}`;
      }
    }
    return "";
  };

  // Auto-open detail view if cid and tid are passed
  useEffect(() => {
    if (cid && tid && onShowDetailView && !hasAutoOpened.current) {
      // Find the index from qtags where category_id = cid and tag_id = tid
      const categoryTags = categories.find(
        (category) => category.catId === parseInt(cid),
      )?.tags;
      let tagIndex = -1;
      if (categoryTags) {
        tagIndex = categoryTags.findIndex((tag) =>
          tag.qtags.find((qtag) => qtag.tagId === parseInt(tid)),
        );
      }

      // Only proceed if we found a valid tag index
      if (tagIndex === -1) {
        return;
      }

      const initialSpaceId = `${cid}-${tagIndex}`;
      hasAutoOpened.current = true;
      AnalyticsService.trackGAEventHandler("accessibilityItemOpen");
      AnalyticsService.trackMx("accessibilityItemOpen", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
        MainTag: qtagsAll.split(",")[0],
      });
      onShowDetailView(initialSpaceId);
    }
  }, [cid, tid, onShowDetailView, venueDetails, qtagsAll, categories]);

  const tags = qtagsAll.split(",");
  const mainTag = tags[0];
  const photos = reviewPhotos;

  // Get all tag names (excluding the main tag for display in bullet list)
  const allTagNames = qtags
    .map((qtag) => qtag.tagName)
    .filter((name) => name !== mainTag);

  const totalTagsCount = qtags.length;

  const handleViewAllClick = () => {
    AnalyticsService.trackGAEventHandler("accessibilityItemOpen");
    AnalyticsService.trackMx("accessibilityItemOpen", {
      VenueId: venueDetails.business.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueType: venueDetails.businessType,
      MainTag: mainTag,
    });
    if (onShowDetailView) {
      onShowDetailView(getInitialSpaceId());
    }
  };

  const handlePhotoScrolled = (dir: "left" | "right") => {
    const eventName =
      dir === "left"
        ? "venueDetailAccessibilityPhotoScrollLeft"
        : "venueDetailAccessibilityPhotoScrollRight";
    AnalyticsService.trackGAEventHandler(eventName);
    AnalyticsService.trackMx(eventName, {
      VenueId: venueDetails?.business?.id,
      VenueName: venueDetails?.name,
    });
  };

  if (mainTag === "No bathroom onsite") {
    return (
      <article className="bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-xl overflow-hidden">
        <div className="p-6 flex items-center justify-center">
          <h3 className="label-l-bold text-fg-neutral-strong text-center">
            {mainTag}
          </h3>
        </div>
      </article>
    );
  }

  return (
    <AccessibilitySpaceCard
      imageMode="carousel"
      photos={photos}
      onPhotoClick={handleViewAllClick}
      onPhotoScrolled={handlePhotoScrolled}
      title={mainTag}
      titleClassName="label-l-bold"
      subtitle={
        latestReviewDetailUpdatedDate?.latest_review_posted_at ? (
          <p className="label-s-medium text-fg-neutral-subtle">
            {i18n.get("lastUpdated")}:{" "}
            {formatFullDateAsShort(
              latestReviewDetailUpdatedDate.latest_review_posted_at,
            )}
          </p>
        ) : undefined
      }
      tagLines={allTagNames}
      emptyTagMessage={`• ${i18n.get("noAccessibilityInformationAvailable") || "No additional accessibility information available"}`}
      tagsCount={totalTagsCount}
      onContentClick={handleViewAllClick}
      contentAriaLabel={`${i18n.get("viewAll") || "View all"} ${totalTagsCount} ${i18n.get("accessibilityTags") || "accessibility tags"} ${i18n.get("for") || "for"} ${mainTag}`}
      viewAllLabel={i18n.get("viewAll") || "View all"}
      accessibilityTagsLabel={i18n.get("accessibilityTags") || "accessibility tags"}
    />
  );
}

export default AccessibilitySubItem;
